import { isEqual } from 'lodash';
import React, { ChangeEvent } from 'react';
import { PermissionsType, SystemRolesName, SystemRolesType } from 'src/api';
import { User, Validation } from 'src/types';
import * as Validator from 'validator';
import * as Types from '../../../types';

export const initialValidation = {
  name: true,
  email: true,
  role: true,
  department: true,
  site: true,
  eid: true,
  country: true,
  displayName: true,
};

export const validateState = (
  user: User,
  validation: Validation,
  setValidation: (validation: Validation) => void,
): boolean => {
  const newValidation = initialValidation;

  Validator.isEmpty(user.name) && (validation.name = false);
  Validator.isEmpty(user.role) && (validation.role = false);
  Validator.isEmpty(user.site) && (validation.site = false);
  Validator.isEmpty(user.eid) && (validation.eid = false);
  Validator.isEmpty(user.department) && (validation.department = false);
  !Validator.isEmail(user.email) && (validation.email = false);

  setValidation({
    ...newValidation,
    ...validation,
  });

  const stateIsValid = Object.values(validation).every(
    (value) => value === true,
  );
  return stateIsValid;
};

export const handleToggle = (
  selectedPermissions: PermissionsType,
  currentUser: Types.User | null,
  user: Types.User,
  setUser: (value: React.SetStateAction<User>) => void,
) => {
  if (!currentUser?.permissions.includes(PermissionsType.ADMIN)) {
    return;
  }

  if (
    user.permissions.includes(PermissionsType.SUPER) &&
    selectedPermissions === PermissionsType.ADMIN
  ) {
    return;
  }

  const permissions = user.permissions.includes(selectedPermissions)
    ? user.permissions.filter(
        (elem: PermissionsType) => elem !== selectedPermissions,
      )
    : [...user.permissions, selectedPermissions];

  if (permissions.includes(PermissionsType.SUPER)) {
    if (!permissions.includes(PermissionsType.ADMIN)) {
      permissions.push(PermissionsType.ADMIN);
    }
  }

  // fix reactivation of user with app permission
  const isAppUser = Boolean(
    user.Accounts.some((account) => account.kind === Types.AccountType.App),
  );
  if (isAppUser && !permissions.includes(PermissionsType.APP)) {
    permissions.push(PermissionsType.APP);
  }

  setUser({
    ...user,
    permissions,
    displayName: permissions.includes(PermissionsType.MANAGER)
      ? user.displayName
      : null,
  });
};

export const handleSystemRolesToggle = (
  selectedPermissions: SystemRolesType,
  currentUser: Types.User | null,
  user: Types.User,
  adminPermission: SystemRolesType | undefined,
  setUser: (value: React.SetStateAction<User>) => void,
) => {
  if (!currentUser || !user.SystemRoles) return;
  if (!currentUser.systemPermissions?.includes('MANAGE_USERS')) return;
  if (
    (user.SystemRoles.find((role) => role.name === SystemRolesName.SUPER) ||
      user.SystemRoles.find(
        (role) => role.name === SystemRolesName.COI_SUPER,
      )) &&
    selectedPermissions.name === SystemRolesName.ADMIN
  ) {
    return;
  }

  const formatPermission = {
    id: selectedPermissions.id,
    name: selectedPermissions.name,
    label: selectedPermissions.label,
    publicSystemPermissions: selectedPermissions.publicSystemPermissions,
  };

  const systemRoles = user.SystemRoles.find(
    (role) => role.id === selectedPermissions.id,
  )
    ? user.SystemRoles.filter(
        (elem: SystemRolesType) => elem.id !== selectedPermissions.id,
      )
    : [...user.SystemRoles, formatPermission];

  if (
    systemRoles.find((role) => role.name === SystemRolesName.SUPER) ||
    systemRoles.find((role) => role.name === SystemRolesName.COI_SUPER)
  ) {
    if (
      adminPermission &&
      !systemRoles.find((role) => role.name === SystemRolesName.ADMIN)
    ) {
      systemRoles.push({
        id: adminPermission.id,
        name: adminPermission.name,
        label: adminPermission.label,
        publicSystemPermissions: adminPermission.publicSystemPermissions,
      });
    }
  }

  const permissions = systemRoles
    .filter((role) => role.name.startsWith('DEFAULT ROLE'))
    .map((role) => role.name.split(' ').pop() as PermissionsType);

  const isAppUser = Boolean(
    user.permissions.includes(PermissionsType.APP) ||
    user.Accounts.some((account) => account.kind === Types.AccountType.App),
  );

  if (isAppUser && !permissions.includes(PermissionsType.APP)) {
    permissions.push(PermissionsType.APP);
  }
  setUser({
    ...user,
    SystemRoles: systemRoles,
    permissions,
    displayName: user.displayName ?? null,
  });
};

const validateTextBoxChange = (key: string, value: string): boolean => {
  const SPECIAL_CHAR_REGEX = /^[=+\-@]/;
  if (key === 'email') return Validator.isEmail(value);
  // Restrict any special character in the value
  if (key !== 'eid' && SPECIAL_CHAR_REGEX.test(value)) {
    return false;
  }
  if (key === 'displayName' || key === 'country') return true;
  else return !Validator.isEmpty(value);
};

export const useOnTextBoxChange = (
  event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  user: Types.User,
  setUser: (value: React.SetStateAction<User>) => void,
  validation: Validation,
  setValidation: (value: React.SetStateAction<Validation>) => void,
) => {
  const key: string = event.currentTarget.id;
  const { value } = event.currentTarget;

  const valid = validateTextBoxChange(key, value);

  setValidation({ ...validation, [key]: valid } as Validation);
  setUser({ ...user, [key]: value });
};

export const getEditablePermissions = (
  currentUser: Types.User | null,
  user: Types.User,
) =>
  currentUser &&
  currentUser.systemPermissions &&
  currentUser.id &&
  currentUser.systemPermissions.includes('MANAGE_USERS') &&
  (+currentUser.id !== user.id ||
    currentUser.systemPermissions.includes('ASSIGN_SELF_OTHER_ROLES'));

export const canToggleSuperPermission = (
  currentUser: Types.User | null,
  user: Types.User,
) => {
  const userIsNotSelf = currentUser?.id !== user.id?.toString();

  const userSystemRoles = user.SystemRoles.map((role) => role.name);
  const currentUserSystemRoles = currentUser?.SystemRoles?.map(
    (role) => role.name,
  );

  const addedSuperRole =
    currentUserSystemRoles?.includes(SystemRolesName.ADMIN) &&
    userSystemRoles.includes(SystemRolesName.SUPER);
  const addCoiSuperRole =
    currentUserSystemRoles?.includes(SystemRolesName.ADMIN) &&
    userSystemRoles.includes(SystemRolesName.COI_SUPER);
  const adminCanToggleOffSuper =
    addedSuperRole ||
    addCoiSuperRole ||
    currentUserSystemRoles?.includes(SystemRolesName.SUPER) ||
    currentUserSystemRoles?.includes(SystemRolesName.COI_SUPER);
  return adminCanToggleOffSuper && userIsNotSelf;
};

export const canToggleAdminPermission = (
  currentUser: Types.User | null,
  user: Types.User,
) => {
  const userIsNotSelf = currentUser?.id !== user.id?.toString();

  const userSystemRoles = user.SystemRoles.map((role) => role.name);
  return (
    !userSystemRoles.includes(SystemRolesName.SUPER) &&
    !userSystemRoles.includes(SystemRolesName.COI_SUPER) &&
    userIsNotSelf
  );
};

export const createAccounts = (user: User) => {
  const accounts = [];
  const portalAccounts = user.Accounts.filter(
    (account) => account.kind === Types.AccountType.Portal,
  );
  const appAccounts = user.Accounts.filter(
    (account) => account.kind === Types.AccountType.App,
  );

  if (portalAccounts.length) {
    accounts.push(...portalAccounts);
  } else if (
    user.SystemRoles.length > 0 ||
    // Create a portal account if no existing account (temporary keep till sc-46625 is completed)
    user.permissions.includes(PermissionsType.SUPER) ||
    user.permissions.includes(PermissionsType.ADMIN) ||
    user.permissions.includes(PermissionsType.MANAGER)
  ) {
    accounts.push({
      kind: Types.AccountType.Portal,
      username: user.email,
      user: user.id,
    });
  }

  if (appAccounts.length) {
    accounts.push(...appAccounts);
  } else if (user.permissions.includes(PermissionsType.APP)) {
    // Create an app account if no existing account
    accounts.push({
      kind: Types.AccountType.App,
      username: user.email,
      user: user.id,
    });
  }
  return accounts;
};

export const isDifferenceInUserDetails = (
  user: User,
  selectedUser: Partial<User> | null,
): boolean => {
  if (!selectedUser) return false;

  const {
    Accounts: userAccounts,
    permissions: userPermissions,
    ...userDetails
  } = user;
  const {
    Accounts: selectedUserAccounts,
    permissions: selectedUserPermissions,
    ...selectedUserDetails
  } = selectedUser;

  return !isEqual(userDetails, selectedUserDetails);
};

export const differenceInUserDetails = (
  user: User,
  selectedUser: Partial<User> | null,
): Types.UserDetailAuditEntry[] => {
  if (!selectedUser) return [];

  const keysToSkip = ['Accounts', 'permissions'];
  const difference: Types.UserDetailAuditEntry[] = [];

  Object.keys(user).forEach((key) => {
    const userProperty = user[key as keyof User];
    const selectedUserProperty = selectedUser[key as keyof User];
    const userPropertyHasChanged =
      userProperty !== selectedUserProperty && !keysToSkip.includes(key);

    if (userPropertyHasChanged) {
      difference.push({
        key,
        old: selectedUserProperty?.toString(),
        new: userProperty?.toString(),
      });
    }
  });

  return difference;
};
